import React, { useEffect, useState } from "react";
import { EventData } from "../../interface/event.interface";
import { useUserActive, useUserRole } from "../../hooks/useUserRole.hook";
import { storageConst } from "../../utils/constants";
import { useNavigate } from "react-router";
import Button from "../../components/Button";
import { Search } from "lucide-react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../lib/firebase";
import EventCard from "../../components/eventCard";
import AddEvent from "./components/addEvent";
import SkeletonCard from "../../components/skeltonCard";

// Haversine formula to calculate distance between two lat/lng points in km
function getDistanceFromLatLonInKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  const R = 6371; // Radius of the earth in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    0.5 -
    Math.cos(((lat2 - lat1) * Math.PI) / 180) / 2 +
    (Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      (1 - Math.cos(((lon2 - lon1) * Math.PI) / 180))) /
      2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

const AllEvents = () => {
  const [filter, setFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLogin, setIsLogin] = useState(false);
  const [events, setEvents] = useState<EventData[]>([]);
  const [openAddEventModal, setOpenAddEventModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const userRole = useUserRole(storageConst.APPTOKEN);
  const userActive = useUserActive(storageConst.APPTOKEN);
  const navigate = useNavigate();
  // Prompt for user location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          setUserLocation(null); // User denied or error
        }
      );
    }
  }, []);

  useEffect(() => {
    setIsLogin(localStorage.getItem(storageConst.APPTOKEN) ? true : false);
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = today.toISOString().split("T")[0]; // 'YYYY-MM-DD'

        const eventsCollection = collection(db, "products");
        const eventsQuery = query(
          eventsCollection,
          where("publish", "==", true),
          where("startdate", ">=", todayStr),
          where("enddate", ">=", todayStr)
        );

        const eventSnapshot = await getDocs(eventsQuery);
        const eventList = eventSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as any),
        })); // JS filter for startdate
        // If user location is available, sort by distance, else by date
        if (userLocation) {
          eventList.forEach((event) => {
            if (
              typeof event.latitude === "number" &&
              typeof event.longitude === "number"
            ) {
              event._distance = getDistanceFromLatLonInKm(
                userLocation.lat,
                userLocation.lng,
                event.latitude,
                event.longitude
              );
            } else {
              event._distance = Infinity;
            }
          });
          eventList.sort(
            (a, b) => (a._distance ?? Infinity) - (b._distance ?? Infinity)
          );
        } else {
          // Sort events by startdate ascending (soonest first)
          eventList.sort((a, b) => {
            const aDate = a.startdate ? new Date(a.startdate) : new Date(0);
            const bDate = b.startdate ? new Date(b.startdate) : new Date(0);
            return aDate.getTime() - bDate.getTime();
          });
        }
        setEvents(eventList);
      } catch (error) {
        console.error("Error fetching events: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [userRole, userLocation]);
  const filteredEvents = events.filter((event: EventData) => {
    const eventName = event.eventname || "";
    const eventAddress = event.addressdetail || "";

    const lowerSearch = searchTerm.toLowerCase();
    const matchesSearch =
      eventName.toLowerCase().includes(lowerSearch) ||
      eventAddress.toLowerCase().includes(lowerSearch);
    const matchesFilter =
      filter === "all" ||
      (filter === "publish" && event.publish) ||
      (filter === "unpublish" && !event.publish);
    return matchesSearch && matchesFilter;
  });
  const handleOpenModal = () => setOpenAddEventModal(true);
  const handleCloseModal = () => setOpenAddEventModal(false);
  const handleLoginRedirect = () => {
    navigate("/signin");
  };
  return (
    <div>
      <div>
        {openAddEventModal && (
          <AddEvent open={openAddEventModal} onClose={handleCloseModal} />
        )}
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-8 space-y-4 sm:space-y-0">
          <div className="flex items-center w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or location"
                // className="pl-10 pr-4 py-2 w-full sm:w-64 bg-background border-gray-700 rounded-full focus:ring-2 focus:ring-primary transition-all"
                className=" w-full sm:min-w-96 pl-9 pr-2 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          {/* Dropdown and Add Button */}

          <div className="flex items-center space-x-4">
            {!isLogin && (
              <Button
                label="Sign In"
                onClick={handleLoginRedirect}
                className="rounded-full"
              ></Button>
            )}
            {userRole === "ADMIN" && (
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="bg-bgCard border-bg rounded-full py-2 px-2 focus:ring-2 focus:ring-primary"
              >
                <option value="all">All</option>
                <option value="publish">Published</option>
                <option value="unpublish">Unpublished</option>
              </select>
            )}
            {isLogin && userActive && (
              <Button
                label="Add Event"
                onClick={handleOpenModal}
                className="rounded-full"
              ></Button>
            )}
          </div>
        </div>

        {/* Events List */}

        {/* <div>
          {filteredEvents.length > 0 ? (
            <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
              {filteredEvents.map((event, index) => (
                <EventCard item={event} key={index} />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 flex justify-center">
              No events found.
            </p>
          )}
        </div> */}
        <div>
          {loading ? (
            // Show skeletons during loading
            <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
              {Array.from({ length: 8 }).map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          ) : filteredEvents.length > 0 ? (
            // Show actual events after loading
            <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
              {filteredEvents.map((event, index) => (
                <EventCard item={event} key={index} />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 flex justify-center">
              No events found.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllEvents;
